import { Component, Host, h, Method, Listen, State, Event, EventEmitter } from '@stencil/core';

export interface ToolState {
  tool: ToolComponent;
  isValid: boolean;
}

export interface ToolComponent {
  launch: () => Promise<void>;
}

@Component({
  tag: 'cr-toolbox',
  styleUrl: 'cr-toolbox.css',
  shadow: true,
})
export class CrToolbox {
  @State() expanded: boolean = false;
  @State() tools: Map<string, ToolState> = new Map();

  @Event() toolboxOpened: EventEmitter<null>;
  @Event() toolboxClosed: EventEmitter<null>;
  @Event() toolAdded: EventEmitter<ToolComponent>;
  @Event() toolRemoved: EventEmitter<ToolComponent>;

  @Method() async show() {
    this.expanded = true;
    this.toolboxOpened.emit();
  }
  @Method() async hide() {
    this.expanded = false;
    this.toolboxClosed.emit();
  }
  @Method() async addTool(label: string, tool: ToolComponent) {
    this.registerTool(label, tool);
    this.toolAdded.emit(tool)
  }
  @Method() async launch(label: string) {
    this.launchTool(label);
  }
  @Method() async removeTool(label: string): Promise<ToolComponent> {
    const { tool } = this.tools.get(label);
    this.deregisterTool(label);
    this.toolRemoved.emit(tool)
    return tool;
  }

  @Listen('keydown', { target: 'window' })
  handleEvent(e: KeyboardEvent) {
    this.handleKeyDown(e);
  }
  @Listen('toolLinkClicked')
  handleLinkedClicked(event: CustomEvent<string>) {
    this.launchTool(event.detail);
  }

  render() {
    return (
      <Host>
        <bx-side-nav aria-label="Side navigation" class={`side-nav ${this.expanded ? 'expanded' : 'hidden'}`} collapseMode="fixed">
          <h3 class="side-nav-header">Tools</h3>
          <bx-side-nav-items class="side-nav-items">
            {[...this.tools].map(([label, { isValid }]) => {
              return <cr-tool-link linkTitle={label} isValid={isValid}></cr-tool-link>;
            })}
          </bx-side-nav-items>
        </bx-side-nav>
      </Host>
    );
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'L' && e.ctrlKey && e.shiftKey) {
      if (this.expanded) this.hide();
      else this.show();
    }
  }

  private launchTool(label: string) {
    const { tool, isValid } = this.tools.get(label);
    if (!isValid) return;
    try {
      tool.launch();
      if (this.expanded) this.hide();
    } catch (error) {
      this.invalidateTool(label);
    }
  }

  private registerTool(label: string, tool: ToolComponent) {
    if (this.missingOrInvalidLabel(label)) {
      return;
    } else if (this.toolRegistered(label)) {
      this.replaceTool(label, tool)
      return;
    } else if (this.missingOrInvalidTool(tool)) {
      this.setToolState(label, tool, false);
      return;
    } else {
      this.setToolState(label, tool, true);
      return;
    }
  }

  private missingOrInvalidTool(tool: any): boolean {
    return !Boolean(tool) || !Boolean(tool.launch);
  }

  private toolRegistered(label: string): boolean {
    return this.tools.has(label);
  }

  private invalidateTool(label: string) {
    const { tool } = this.tools.get(label);
    this.tools = new Map(this.tools.set(label, { tool, isValid: false }));
  }

  private deregisterTool(label: string): void {
    this.tools.delete(label);
    this.tools = new Map(this.tools);
  }

  private missingOrInvalidLabel(label: string): boolean {
    return typeof label !== 'string' || label.length < 1;
  }
  private setToolState(label: string, tool: ToolComponent, isValid: boolean) {
    this.tools = new Map(this.tools.set(label, { tool, isValid }));
  }

  private replaceTool(label: string, newTool: ToolComponent) {
    const duplicateTool = this.tools.get(label);
    if (!duplicateTool.isValid && !this.missingOrInvalidTool(newTool)) {
      this.setToolState(label, newTool, true);
    }
  }
}
